import { supabaseClient } from "@/lib/supabase";
import { queryOptions } from "@tanstack/react-query";

export interface OrganisationUser {
    user_id: string;
    name: string;
    email: string;
    roles: string[];
}

export async function selectUsersByOrganisationId(organisationId: string): Promise<OrganisationUser[]> {
    const { data, error } = await supabaseClient
        .from("users_organisations_roles")
        .select(`
			user_id,
			role,
			profiles!users_organisations_roles_user_id_fkey(
				user_id,
				email,
				first_name,
				last_name,
				name,
				roles
			)
		`)
        .eq("organisation_id", organisationId);

    if (error) throw error;

    // Group users by user_id and collect all their roles for this organisation
    const userMap = new Map<string, OrganisationUser>();

    for (const row of data || []) {
        const profile = row.profiles;
        if (!profile || !row.user_id) continue;

        const userId = row.user_id;
        const existingUser = userMap.get(userId);

        if (existingUser) {
            // Add role if not already present
            if (!existingUser.roles.includes(row.role)) {
                existingUser.roles.push(row.role);
            }
        } else {
            // Create new user entry
            // Use name from profile view if available, otherwise construct from first_name/last_name
            const name =
                (profile as any).name ||
                (profile.first_name && profile.last_name
                    ? `${profile.first_name} ${profile.last_name}`.trim()
                    : profile.email || "");

            userMap.set(userId, {
                user_id: userId,
                name: name,
                email: profile.email || "",
                roles: [row.role],
            });
        }
    }

    return Array.from(userMap.values());
}

export const selectUsersByOrganisationIdQueryOptions = (organisationId: string) =>
    queryOptions({
        queryKey: ["organisation-users", organisationId],
        queryFn: async () => selectUsersByOrganisationId(organisationId),
    });
